import { Server } from 'lucide-react';
import { useMemo } from 'react';

import { IndexOptional } from '@/react/kubernetes/configs/types';
import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';

import { ApplicationPod } from '../ListView/ApplicationsDatatable/types';

import {
  ContainerStatusInfo,
  PodRowData,
} from './ApplicationContainersDatatable/types';
import { podColumns } from './ApplicationContainersDatatable/columns/pod';

const storageKey = 'k8sCustomResourcePodsDatatable';
const settingsStore = createStore(storageKey);

export function CustomResourcePodsDatatable({
  pods,
}: {
  pods: ApplicationPod[];
}) {
  const tableState = useTableState(settingsStore, storageKey);

  const podRowData = useCustomResourcePodRowData(pods);

  return (
    <Datatable<IndexOptional<PodRowData>>
      dataset={podRowData}
      columns={podColumns}
      settingsManager={tableState}
      title="Custom Resource pods"
      titleIcon={Server}
      getRowId={(row) => row.podName}
      disableSelect
      data-cy="k8s-custom-resource-containers-datatable"
    />
  );
}

// Each ApplicationPod row carries one container's worth of denormalised info, so
// the sub-row table is collapsed to a single-container summary at the pod level.
function useCustomResourcePodRowData(pods: ApplicationPod[]): PodRowData[] {
  return useMemo(
    () =>
      pods.map((pod) => {
        const status = getStatusType(pod.Status);
        const isReady = status.type === 'success';
        return {
          podName: pod.Name,
          nodeName: pod.NodeName,
          podIp: pod.PodIP,
          creationDate: pod.CreationDate,
          status,
          containers: [
            {
              name: pod.ContainerName,
              image: pod.Image,
              imagePullPolicy: pod.ImagePullPolicy,
              podName: pod.Name,
              status,
            },
          ],
          readyContainers: isReady ? 1 : 0,
          totalContainers: 1,
        };
      }),
    [pods]
  );
}

// https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-phase
function getStatusType(status: string): ContainerStatusInfo {
  switch (status) {
    case 'Pending':
      return { status: 'Pending', type: 'muted' };
    case 'Running':
      return { status: 'Running', type: 'success' };
    case 'Succeeded':
      return { status: 'Succeeded', type: 'success' };
    case 'Failed':
      return { status: 'Failed', type: 'danger' };
    case 'Unknown':
    default:
      return { status: 'Unknown', type: 'muted' };
  }
}
